import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { toast } from "sonner";
import {
  Users, Link2, TrendingUp, DollarSign,
  Plus, Copy, Eye, Mail, Percent, Power, Trash2, Key as KeyIcon,
  ChevronDown, X, Pencil, Save,
} from "lucide-react";

const SITE_ROOT = process.env.REACT_APP_PUBLIC_URL || window.location.origin;

export default function AdminAfiliados() {
  const [afiliados, setAfiliados] = useState([]);
  const [stats, setStats] = useState({ afiliados: 0, leads_referidos: 0, convertidos: 0, comision_pendiente: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showLeadsFor, setShowLeadsFor] = useState(null);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    nombre: "", codigo: "", comision_pct: 10, email: "",
    portal_usuario: "", portal_password: "",
    afiliado_padre_id: "",
    cobrandeado: false, logo_url: "", banner_titulo: "", banner_subtexto: "", banner_bg_color: "#1E3FAE",
    banner_titulo_bold: true, banner_subtexto_bold: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: s }] = await Promise.all([
        http.get("/admin/afiliados"),
        http.get("/admin/afiliados/stats"),
      ]);
      setAfiliados(rows || []);
      setStats(s || {});
    } catch {
      toast.error("No se pudo cargar la central de afiliados");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.codigo.trim()) {
      toast.error("Nombre y código son obligatorios");
      return;
    }
    setCreating(true);
    try {
      await http.post("/admin/afiliados", {
        ...form,
        comision_pct: Number(form.comision_pct) || 0,
        afiliado_padre_id: form.afiliado_padre_id || null,
      });
      toast.success(`Afiliado ${form.codigo.toUpperCase()} creado`);
      setForm({
        nombre: "", codigo: "", comision_pct: 10, email: "",
        portal_usuario: "", portal_password: "",
        afiliado_padre_id: "",
        cobrandeado: false, logo_url: "", banner_titulo: "", banner_subtexto: "", banner_bg_color: "#1E3FAE",
        banner_titulo_bold: true, banner_subtexto_bold: false,
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally {
      setCreating(false);
    }
  };

  const toggleActivo = async (a) => {
    try {
      await http.patch(`/admin/afiliados/${a.id}`, { activo: !a.activo });
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const removeAff = async (a) => {
    if (!window.confirm(`¿Eliminar afiliado ${a.codigo}? Sus leads permanecerán pero no podrás editar sus datos.`)) return;
    try {
      await http.delete(`/admin/afiliados/${a.id}`);
      toast.success("Afiliado eliminado");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const changePassword = async (a) => {
    const p = window.prompt(`Nueva contraseña del portal para ${a.nombre}:`);
    if (!p) return;
    try {
      await http.patch(`/admin/afiliados/${a.id}`, { portal_password: p });
      toast.success("Contraseña actualizada");
    } catch { toast.error("No se pudo actualizar"); }
  };

  const copyLink = (a) => {
    const link = `${SITE_ROOT}/${a.codigo}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado al portapapeles");
  };

  return (
    <div className="fade-in-up" data-testid="admin-afiliados">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Central de Afiliados</h1>
        <p className="text-[13.5px] mt-1.5" style={{ color: "var(--text-muted)" }}>
          Crea links de referido (ej: <code className="px-1.5 py-0.5 rounded text-[11.5px]" style={{ background: "var(--bg-soft)" }}>/FC</code>) y revisa los resultados de cada afiliado.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Afiliados" value={stats.afiliados} icon={Users} color="var(--brand-navy)" />
        <StatCard label="Leads referidos" value={stats.leads_referidos} icon={Link2} color="var(--brand-navy)" />
        <StatCard label="Convertidos" value={stats.convertidos} icon={TrendingUp} color="#15803d" />
        <StatCard label="Comisión por cobrar" value={`$${(stats.comision_pendiente || 0).toLocaleString("es-CL")}`} icon={DollarSign} color="#15803d" highlight />
      </div>

      {/* Create form */}
      <div className="bg-white rounded-xl p-6 mb-8" style={{ border: "1px solid var(--border-medium)" }}>
        <div className="flex items-center gap-2 mb-5">
          <Link2 size={18} style={{ color: "var(--brand-navy)" }} />
          <h2 className="text-[16px] font-bold" style={{ fontFamily: "Manrope" }}>Crear afiliado / link de referido</h2>
        </div>

        <form onSubmit={create} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <FieldLabel required>Nombre del afiliado</FieldLabel>
              <input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} className="field-input field-input--compact" placeholder="Franco Contreras" required data-testid="aff-nombre" />
            </div>
            <div>
              <FieldLabel required>Código / Iniciales</FieldLabel>
              <input value={form.codigo} onChange={(e) => setF("codigo", e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase())} className="field-input field-input--compact" placeholder="FC" maxLength={15} required data-testid="aff-codigo" />
            </div>
            <div>
              <FieldLabel>Comisión %</FieldLabel>
              <input type="number" min="0" max="100" step="0.5" value={form.comision_pct} onChange={(e) => setF("comision_pct", e.target.value)} className="field-input field-input--compact" data-testid="aff-comision" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <FieldLabel>Email del afiliado</FieldLabel>
              <input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="field-input field-input--compact" placeholder="franco@ejemplo.cl" data-testid="aff-email" />
            </div>
            <div>
              <FieldLabel>Afiliado padre (opcional)</FieldLabel>
              <select value={form.afiliado_padre_id} onChange={(e) => setF("afiliado_padre_id", e.target.value)} className="field-input field-input--compact" data-testid="aff-padre">
                <option value="">— Sin padre (afiliado principal) —</option>
                {afiliados.map((a) => (
                  <option key={a.id} value={a.id}>{a.codigo} — {a.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Portal access */}
          <div className="rounded-lg p-4" style={{ background: "var(--bg-soft)", border: "1px dashed var(--border-medium)" }}>
            <div className="flex items-center gap-2 mb-3">
              <KeyIcon size={14} style={{ color: "var(--text-secondary)" }}/>
              <span className="text-[11.5px] uppercase tracking-wider font-bold" style={{ color: "var(--text-secondary)" }}>Acceso al portal del afiliado (opcional)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Usuario</FieldLabel>
                <input value={form.portal_usuario} onChange={(e) => setF("portal_usuario", e.target.value.toLowerCase())} className="field-input field-input--compact" placeholder="franco.c" data-testid="aff-portal-user" />
              </div>
              <div>
                <FieldLabel>Clave</FieldLabel>
                <input type="text" value={form.portal_password} onChange={(e) => setF("portal_password", e.target.value)} className="field-input field-input--compact" placeholder="clave para el afiliado" data-testid="aff-portal-pass" />
              </div>
            </div>
            <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
              Con usuario y clave, el afiliado podrá entrar a <code>{SITE_ROOT}/afiliado</code> a ver sus leads y comisión.
            </p>
          </div>

          {/* Cobranding */}
          <div className="rounded-lg p-4" style={{ background: "rgba(238,81,71,0.03)", border: "1px dashed var(--brand-accent)" }}>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={form.cobrandeado} onChange={(e) => setF("cobrandeado", e.target.checked)} data-testid="aff-cobrandeado" />
              <span className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>Sitio co-brandeado (mostrar logo + banner custom en el link)</span>
            </label>
            {form.cobrandeado && (
              <>
                {/* Selector de cobranding existente */}
                {afiliados.some((a) => a.cobrandeado) && (
                  <div className="mb-4 rounded-lg p-3" style={{ background: "white", border: "1px solid var(--border-medium)" }}>
                    <FieldLabel>Reutilizar cobranding existente (opcional)</FieldLabel>
                    <select
                      value=""
                      onChange={(e) => {
                        const src = afiliados.find((x) => x.id === e.target.value);
                        if (!src) return;
                        setF("logo_url", src.logo_url || "");
                        setF("banner_titulo", src.banner_titulo || "");
                        setF("banner_subtexto", src.banner_subtexto || "");
                        setF("banner_titulo_bold", src.banner_titulo_bold !== false);
                        setF("banner_subtexto_bold", !!src.banner_subtexto_bold);
                        toast.success(`Cobranding de ${src.codigo} copiado`);
                      }}
                      className="field-input field-input--compact"
                      data-testid="aff-copy-cobranding"
                    >
                      <option value="">— Elegir plantilla existente (Ñuñoa, Fack, etc.) —</option>
                      {afiliados.filter((a) => a.cobrandeado).map((a) => (
                        <option key={a.id} value={a.id}>{a.codigo} — {a.nombre}</option>
                      ))}
                    </select>
                    <p className="text-[10.5px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                      Al elegir, se copian logo, título y subtexto abajo. Puedes ajustarlos antes de crear.
                    </p>
                  </div>
                )}
                <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
                  El color del banner es fijo (identidad de marca). Título y subtexto son opcionales — puedes dejar solo el logo, solo texto, o combinar.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <FieldLabel>URL del logo <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                  <input value={form.logo_url} onChange={(e) => setF("logo_url", e.target.value)} className="field-input field-input--compact" placeholder="https://..." />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <FieldLabel>Título principal <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                    <label className="inline-flex items-center gap-1 cursor-pointer mb-1">
                      <input type="checkbox" checked={form.banner_titulo_bold} onChange={(e) => setF("banner_titulo_bold", e.target.checked)} className="w-3.5 h-3.5" data-testid="aff-titulo-bold" />
                      <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Negrita</span>
                    </label>
                  </div>
                  <input value={form.banner_titulo} onChange={(e) => setF("banner_titulo", e.target.value)} className="field-input field-input--compact" placeholder="Beneficio exclusivo para..." />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <FieldLabel>Subtexto <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                    <label className="inline-flex items-center gap-1 cursor-pointer mb-1">
                      <input type="checkbox" checked={form.banner_subtexto_bold} onChange={(e) => setF("banner_subtexto_bold", e.target.checked)} className="w-3.5 h-3.5" data-testid="aff-subtexto-bold" />
                      <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Negrita</span>
                    </label>
                  </div>
                  <input value={form.banner_subtexto} onChange={(e) => setF("banner_subtexto", e.target.value)} className="field-input field-input--compact" placeholder="Les ahorramos $... en promedio" />
                </div>
                </div>
              </>
            )}
          </div>

          <button type="submit" disabled={creating} className="btn-primary" data-testid="aff-create">
            <Plus size={15}/> {creating ? "Creando…" : "Crear afiliado y link"}
          </button>
        </form>
      </div>

      {/* Listing */}
      {loading ? (
        <div className="py-12 text-center text-[13px]" style={{ color: "var(--text-muted)" }}>Cargando…</div>
      ) : afiliados.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" style={{ border: "1px solid var(--border-medium)" }}>
          <div className="text-[14px] font-semibold" style={{ color: "var(--text-secondary)" }}>Aún no has creado afiliados</div>
          <div className="text-[12px] mt-1" style={{ color: "var(--text-muted)" }}>Crea el primero con el formulario de arriba.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {afiliados.map((a) => (
            <AffiliateRow
              key={a.id}
              a={a}
              parent={afiliados.find((x) => x.id === a.afiliado_padre_id)}
              onToggle={() => toggleActivo(a)}
              onDelete={() => removeAff(a)}
              onPass={() => changePassword(a)}
              onCopy={() => copyLink(a)}
              onSeeLeads={() => setShowLeadsFor(a)}
              onEdit={() => setEditing(a)}
            />
          ))}
        </div>
      )}

      {showLeadsFor && (
        <LeadsModal afiliado={showLeadsFor} onClose={() => { setShowLeadsFor(null); load(); }} />
      )}
      {editing && (
        <EditAfiliadoModal
          afiliado={editing}
          afiliados={afiliados}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AffiliateRow({ a, parent, onToggle, onDelete, onPass, onCopy, onSeeLeads, onEdit }) {
  const link = `${SITE_ROOT}/${a.codigo}`;
  return (
    <div className="bg-white rounded-xl p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4" style={{ border: `1px solid ${a.activo ? "var(--border-medium)" : "rgba(238,81,71,0.3)"}`, opacity: a.activo ? 1 : 0.65 }} data-testid={`aff-row-${a.codigo}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-[15px]" style={{ fontFamily: "Manrope" }}>{a.nombre}</div>
        <div className="flex flex-wrap gap-1.5 items-center mt-1">
          <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>{a.codigo}</span>
          {a.cobrandeado && (
            <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "rgba(238,81,71,0.1)", color: "var(--brand-accent)" }}>COBRANDEADO</span>
          )}
          {parent && (
            <span className="text-[10.5px] font-bold px-2 py-0.5 rounded" style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }}>↳ padre: {parent.codigo}</span>
          )}
          {a.email && (
            <a href={`mailto:${a.email}`} className="text-[11px] hover:underline" style={{ color: "var(--brand-navy)" }}>
              <Mail size={10} className="inline mr-0.5"/> {a.email}
            </a>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2 text-[11.5px]" style={{ color: "var(--text-muted)" }}>
          <code className="px-2 py-0.5 rounded truncate max-w-xs" style={{ background: "var(--bg-soft)" }}>{link}</code>
          <button onClick={onCopy} className="text-[11px] font-semibold hover:opacity-70" style={{ color: "var(--brand-navy)" }} data-testid={`aff-copy-${a.codigo}`}>
            <Copy size={11} className="inline mr-0.5"/> Copiar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center flex-shrink-0">
        <Metric label="Leads" value={a.leads_count} />
        <Metric label="Convertidos" value={a.convertidos} accent />
        <Metric label="Comisión" value={`$${(a.comision_pendiente || 0).toLocaleString("es-CL")}`} />
      </div>

      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
        <IconBtn onClick={onSeeLeads} icon={Eye} label="Ver leads" testid={`aff-see-${a.codigo}`}/>
        <IconBtn onClick={onEdit} icon={Pencil} label="Editar" testid={`aff-edit-${a.codigo}`}/>
        <IconBtn onClick={onPass} icon={KeyIcon} label="Clave" testid={`aff-pass-${a.codigo}`}/>
        <IconBtn onClick={onToggle} icon={Power} label={a.activo ? "Desactivar" : "Activar"} danger={!a.activo} testid={`aff-toggle-${a.codigo}`}/>
        <IconBtn onClick={onDelete} icon={Trash2} label="" danger testid={`aff-delete-${a.codigo}`}/>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[18px] font-extrabold" style={{ color: accent ? "#15803d" : "var(--brand-navy)", fontFamily: "Manrope" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</div>
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, label, danger, testid }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1.5 rounded transition hover:opacity-80"
      style={{
        background: danger ? "rgba(238,81,71,0.08)" : "var(--bg-soft)",
        color: danger ? "var(--brand-accent)" : "var(--text-secondary)",
      }}
      data-testid={testid}
    >
      <Icon size={12}/> {label && <span>{label}</span>}
    </button>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label className="block text-[11.5px] font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>
      {children}{required && <span style={{ color: "var(--brand-accent)" }}> *</span>}
    </label>
  );
}

function StatCard({ label, value, icon: Icon, color, highlight }) {
  return (
    <div className="bg-white rounded-xl p-4 md:p-5" style={{ border: `1px solid ${highlight ? "rgba(34,197,94,0.3)" : "var(--border-medium)"}`, background: highlight ? "rgba(34,197,94,0.03)" : undefined }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} style={{ color }} />
        <span className="text-[10.5px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</span>
      </div>
      <div className="text-[26px] font-extrabold leading-tight" style={{ color, fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

// ---------------- Leads Modal ----------------
const LEAD_STATUSES = [
  { value: "nuevo",           label: "Nuevo",           color: "#b45309", bg: "rgba(245,158,11,0.15)" },
  { value: "en_cotizacion",   label: "En cotización",   color: "#1e40af", bg: "rgba(59,130,246,0.15)" },
  { value: "en_negociacion",  label: "En negociación",  color: "#7c2d12", bg: "rgba(234,88,12,0.15)" },
  { value: "convertido",      label: "Convertido",      color: "#15803d", bg: "rgba(34,197,94,0.15)" },
  { value: "perdido",         label: "Perdido",         color: "#991b1b", bg: "rgba(239,68,68,0.15)" },
];

function LeadsModal({ afiliado, onClose }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { tipo, id, comision_monto, autoOpenedForConvertido }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/afiliados/${afiliado.id}/leads`);
      setLeads(data || []);
    } catch { toast.error("No se pudieron cargar los leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [afiliado.id]);

  const changeStatus = async (l, newStatus) => {
    try {
      await http.patch(`/admin/lead/${l.tipo}/${l.id}/conversion`, { lead_status: newStatus });
      // Si pasa a Convertido y aún no tiene comisión asignada, abrir el input al toque
      if (newStatus === "convertido" && !l.comision_monto) {
        setEditing({ tipo: l.tipo, id: l.id, comision_monto: "", autoOpenedForConvertido: true });
      }
      load();
    } catch { toast.error("No se pudo actualizar el estado"); }
  };

  const saveComision = async () => {
    if (!editing) return;
    try {
      await http.patch(`/admin/lead/${editing.tipo}/${editing.id}/conversion`, {
        comision_monto: Number(editing.comision_monto) || 0,
        comision_estado: "pendiente",
        lead_status: "convertido",
      });
      toast.success("Comisión asignada");
      setEditing(null);
      load();
    } catch { toast.error("No se pudo asignar"); }
  };

  const markCobrada = async (l) => {
    try {
      await http.patch(`/admin/lead/${l.tipo}/${l.id}/conversion`, { comision_estado: "cobrada" });
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ Borrar TODOS los ${leads.length} leads de ${afiliado.nombre}?\n\nSe moverán a la papelera (Central de Leads → tab Borrados).\nIncluye leads de mensajes de contacto y SPM que no aparecen en la Central de Leads.`)) return;
    try {
      const { data } = await http.delete(`/admin/afiliados/${afiliado.id}/leads`);
      toast.success(`${data.deleted} leads movidos a la papelera`);
      load();
    } catch { toast.error("No se pudieron borrar"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border-medium)" }}>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>Leads del afiliado</div>
            <div className="text-[18px] font-extrabold" style={{ fontFamily: "Manrope" }}>{afiliado.nombre} <span style={{ color: "var(--brand-accent)" }}>({afiliado.codigo})</span></div>
          </div>
          <div className="flex items-center gap-2">
            {leads.length > 0 && (
              <button
                onClick={bulkDelete}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:opacity-80 flex items-center gap-1.5"
                style={{ background: "rgba(239,68,68,0.1)", color: "#991b1b" }}
                data-testid="bulk-delete-leads"
              >
                Borrar todos ({leads.length})
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X size={18}/></button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-[13px]" style={{ color: "var(--text-muted)" }}>Cargando…</div>
          ) : leads.length === 0 ? (
            <div className="py-8 text-center text-[13px]" style={{ color: "var(--text-muted)" }}>Este afiliado aún no tiene leads referidos.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Fecha</th><th>Comunidad</th><th>Mail</th><th>Estado</th><th>Comisión</th><th>Estado comisión</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const status = LEAD_STATUSES.find((s) => s.value === l.lead_status) || LEAD_STATUSES[0];
                  return (
                    <tr key={`${l.tipo}-${l.id}`} data-testid={`lead-row-${l.id}`}>
                      <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>{(l.fecha || "").slice(0, 10)}</td>
                      <td>
                        <div className="font-semibold text-[13px]">{l.detalle || l.nombre}</div>
                        {l.detalle && <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Contacto: {l.nombre}</div>}
                      </td>
                      <td className="text-[12px]">{l.email}<br/><span style={{ color: "var(--text-muted)" }}>{l.telefono}</span></td>
                      <td>
                        <select
                          value={l.lead_status || "nuevo"}
                          onChange={(e) => changeStatus(l, e.target.value)}
                          className="text-[11.5px] font-bold px-2 py-1 rounded border cursor-pointer"
                          style={{ background: status.bg, color: status.color, borderColor: status.color + "40" }}
                          data-testid={`lead-status-${l.id}`}
                        >
                          {LEAD_STATUSES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {editing?.tipo === l.tipo && editing?.id === l.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={editing.comision_monto}
                              onChange={(e) => setEditing({ ...editing, comision_monto: e.target.value })}
                              className="w-24 px-2 py-1 rounded border text-[12px]"
                              style={{ borderColor: "var(--border-medium)" }}
                              placeholder="CLP"
                              autoFocus
                              data-testid={`lead-comision-input-${l.id}`}
                            />
                            <button onClick={saveComision} className="text-[11px] font-bold px-2 py-1 rounded" style={{ background: "var(--brand-navy)", color: "white" }} data-testid={`lead-comision-save-${l.id}`}>OK</button>
                            <button onClick={() => setEditing(null)} className="text-[11px] px-1"><X size={12}/></button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditing({ tipo: l.tipo, id: l.id, comision_monto: l.comision_monto || "" })}
                            className="text-[12px] font-semibold hover:underline"
                            style={{ color: l.comision_monto ? "#15803d" : "var(--brand-navy)" }}
                            data-testid={`lead-comision-btn-${l.id}`}
                          >
                            {l.comision_monto ? `$${Number(l.comision_monto).toLocaleString("es-CL")}` : "Asignar"}
                          </button>
                        )}
                      </td>
                      <td>
                        {l.comision_estado === "pendiente" && (
                          <button onClick={() => markCobrada(l)} className="text-[10.5px] font-bold px-2 py-1 rounded hover:opacity-80" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }} data-testid={`lead-mark-paid-${l.id}`}>POR PAGAR — Marcar pagado</button>
                        )}
                        {l.comision_estado === "cobrada" && (
                          <span className="text-[10.5px] font-bold px-2 py-1 rounded" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>PAGADO</span>
                        )}
                        {(!l.comision_estado || l.comision_estado === "no_aplica") && (
                          <span className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Edit Afiliado Modal ----------------
function EditAfiliadoModal({ afiliado, afiliados, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: afiliado.nombre || "",
    email: afiliado.email || "",
    comision_pct: afiliado.comision_pct ?? 10,
    afiliado_padre_id: afiliado.afiliado_padre_id || "",
    portal_usuario: afiliado.portal_usuario || "",
    portal_password: "",
    cobrandeado: !!afiliado.cobrandeado,
    logo_url: afiliado.logo_url || "",
    banner_titulo: afiliado.banner_titulo || "",
    banner_subtexto: afiliado.banner_subtexto || "",
    banner_bg_color: afiliado.banner_bg_color || "#1E3FAE",
    banner_titulo_bold: afiliado.banner_titulo_bold !== false,
    banner_subtexto_bold: !!afiliado.banner_subtexto_bold,
    logo_invert_header: !!afiliado.logo_invert_header,
    activo: afiliado.activo !== false,
  });
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Only send fields that changed OR non-empty for password
      const patch = {
        nombre: form.nombre.trim(),
        email: form.email.trim() || null,
        comision_pct: Number(form.comision_pct) || 0,
        afiliado_padre_id: form.afiliado_padre_id || null,
        portal_usuario: form.portal_usuario.trim() || null,
        cobrandeado: form.cobrandeado,
        logo_url: form.logo_url.trim() || null,
        banner_titulo: form.banner_titulo.trim() || null,
        banner_subtexto: form.banner_subtexto.trim() || null,
        banner_bg_color: form.banner_bg_color,
        banner_titulo_bold: form.banner_titulo_bold,
        banner_subtexto_bold: form.banner_subtexto_bold,
        logo_invert_header: form.logo_invert_header,
        activo: form.activo,
      };
      if (form.portal_password.trim()) {
        patch.portal_password = form.portal_password.trim();
      }
      await http.patch(`/admin/afiliados/${afiliado.id}`, patch);
      toast.success(`Afiliado ${afiliado.codigo} actualizado`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border-medium)" }}>
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>Editar afiliado</div>
            <div className="text-[18px] font-extrabold" style={{ fontFamily: "Manrope" }}>
              {afiliado.nombre} <span style={{ color: "var(--brand-accent)" }}>({afiliado.codigo})</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-black/5 rounded-lg"><X size={18}/></button>
        </div>

        <form onSubmit={save} className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <FieldLabel required>Nombre del afiliado</FieldLabel>
              <input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} className="field-input field-input--compact" required data-testid="edit-nombre"/>
            </div>
            <div>
              <FieldLabel>Comisión %</FieldLabel>
              <input type="number" min="0" max="100" step="0.5" value={form.comision_pct} onChange={(e) => setF("comision_pct", e.target.value)} className="field-input field-input--compact" data-testid="edit-comision"/>
            </div>
            <div>
              <FieldLabel>Email del afiliado</FieldLabel>
              <input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} className="field-input field-input--compact" placeholder="ejemplo@correo.cl" data-testid="edit-email"/>
            </div>
            <div>
              <FieldLabel>Afiliado padre</FieldLabel>
              <select value={form.afiliado_padre_id} onChange={(e) => setF("afiliado_padre_id", e.target.value)} className="field-input field-input--compact">
                <option value="">— Sin padre —</option>
                {afiliados.filter((x) => x.id !== afiliado.id).map((a) => (
                  <option key={a.id} value={a.id}>{a.codigo} — {a.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Portal */}
          <div className="rounded-lg p-4" style={{ background: "var(--bg-soft)", border: "1px dashed var(--border-medium)" }}>
            <div className="flex items-center gap-2 mb-3">
              <KeyIcon size={14} style={{ color: "var(--text-secondary)" }}/>
              <span className="text-[11.5px] uppercase tracking-wider font-bold" style={{ color: "var(--text-secondary)" }}>Acceso al portal</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Usuario</FieldLabel>
                <input value={form.portal_usuario} onChange={(e) => setF("portal_usuario", e.target.value.toLowerCase())} className="field-input field-input--compact" placeholder="usuario.afiliado" data-testid="edit-portal-user"/>
              </div>
              <div>
                <FieldLabel>Nueva clave <span className="opacity-60 font-normal">(dejar vacío para no cambiar)</span></FieldLabel>
                <input type="text" value={form.portal_password} onChange={(e) => setF("portal_password", e.target.value)} className="field-input field-input--compact" placeholder="nueva contraseña" data-testid="edit-portal-pass"/>
              </div>
            </div>
          </div>

          {/* Cobranding */}
          <div className="rounded-lg p-4" style={{ background: "rgba(238,81,71,0.03)", border: "1px dashed var(--brand-accent)" }}>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={form.cobrandeado} onChange={(e) => setF("cobrandeado", e.target.checked)} data-testid="edit-cobrandeado"/>
              <span className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>Sitio co-brandeado</span>
            </label>
            {form.cobrandeado && (
              <>
                <p className="text-[11px] mb-3" style={{ color: "var(--text-muted)" }}>
                  El color del banner es fijo. Título y subtexto son opcionales.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <FieldLabel>URL del logo <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                    <input value={form.logo_url} onChange={(e) => setF("logo_url", e.target.value)} className="field-input field-input--compact" placeholder="https://..."/>
                    <label className="inline-flex items-center gap-2 cursor-pointer mt-2">
                      <input type="checkbox" checked={form.logo_invert_header} onChange={(e) => setF("logo_invert_header", e.target.checked)} className="w-3.5 h-3.5" data-testid="edit-logo-invert"/>
                      <span className="text-[11.5px]" style={{ color: "var(--text-secondary)" }}>
                        Logo blanco — oscurecerlo en el header <span className="opacity-70">(cuando el archivo es blanco pensado para fondo oscuro)</span>
                      </span>
                    </label>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <FieldLabel>Título principal <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                      <label className="inline-flex items-center gap-1 cursor-pointer mb-1">
                        <input type="checkbox" checked={form.banner_titulo_bold} onChange={(e) => setF("banner_titulo_bold", e.target.checked)} className="w-3.5 h-3.5" data-testid="edit-titulo-bold"/>
                        <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Negrita</span>
                      </label>
                    </div>
                    <input value={form.banner_titulo} onChange={(e) => setF("banner_titulo", e.target.value)} className="field-input field-input--compact"/>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <FieldLabel>Subtexto <span className="opacity-60 font-normal">(opcional)</span></FieldLabel>
                      <label className="inline-flex items-center gap-1 cursor-pointer mb-1">
                        <input type="checkbox" checked={form.banner_subtexto_bold} onChange={(e) => setF("banner_subtexto_bold", e.target.checked)} className="w-3.5 h-3.5" data-testid="edit-subtexto-bold"/>
                        <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>Negrita</span>
                      </label>
                    </div>
                    <input value={form.banner_subtexto} onChange={(e) => setF("banner_subtexto", e.target.value)} className="field-input field-input--compact"/>
                  </div>
                </div>
              </>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.activo} onChange={(e) => setF("activo", e.target.checked)} data-testid="edit-activo"/>
            <span className="text-[13px] font-semibold">Afiliado activo</span>
          </label>

          <div className="flex justify-end gap-2 pt-3 border-t" style={{ borderColor: "var(--border-medium)" }}>
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold" style={{ background: "var(--bg-soft)", color: "var(--text-secondary)" }}>Cancelar</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="edit-save">
              <Save size={14}/> {busy ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

